import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { emitCartUpdated, getDeliveryLabel } from "../utils/marketplace";
import { trackViewCart } from "../utils/analytics";

const getCart = () => {
  try {
    return JSON.parse(localStorage.getItem("cart") || "[]");
  } catch {
    return [];
  }
};

const saveCart = (cart) => {
  localStorage.setItem("cart", JSON.stringify(cart));
};

const getMaxQty = (item) => {
  const stock = Number(item?.stockQty);
  return Number.isFinite(stock) && stock > 0 ? stock : Infinity;
};

const Cart = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);

  useEffect(() => {
    setCart(getCart());
  }, []);

  useEffect(() => {
    if (cart.length) {
      const value = cart.reduce(
        (sum, item) => sum + Number(item.price || 0) * Number(item.qty || 1),
        0
      );
      trackViewCart(cart, value);
    }
  }, [cart]);

  const subtotal = useMemo(() => {
    return cart.reduce(
      (sum, item) => sum + Number(item.price) * Number(item.qty || 1),
      0
    );
  }, [cart]);

  const updateQty = (id, qty) => {
    const currentItem = cart.find((i) => i._id === id);
    const maxQty = getMaxQty(currentItem);
    const requestedQty = Math.max(1, Number(qty || 1));
    const newQty = Math.min(requestedQty, maxQty);

    const next = cart.map((i) =>
      i._id === id ? { ...i, qty: newQty } : i
    );

    setCart(next);
    saveCart(next);
    emitCartUpdated();
  };

  const removeItem = (id) => {
    const next = cart.filter((i) => i._id !== id);
    setCart(next);
    saveCart(next);
    emitCartUpdated();
  };

  const clearCart = () => {
    localStorage.removeItem("cart");
    setCart([]);
    emitCartUpdated();
  };

  if (!cart.length) {
    return (
      <div style={{ maxWidth: 900, margin: "auto", padding: 20 }}>
        <h2>Your Cart</h2>

        <p>Your cart is empty.</p>

        <button
          onClick={() => navigate("/")}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "none",
            background: "#111827",
            color: "#fff",
            cursor: "pointer"
          }}
        >
          Continue Shopping
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: "auto", padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h2>Your Cart</h2>

        <button
          onClick={clearCart}
          style={{
            padding: "8px 12px",
            borderRadius: 10,
            border: "1px solid #ddd",
            background: "#fff",
            color: "#0f172a",
            cursor: "pointer"
          }}
        >
          Clear Cart
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gap: 14,
          gridTemplateColumns: "1fr 320px"
        }}
      >
        {/* Cart Items */}
        <div
          style={{
            padding: 14,
            border: "1px solid #eee",
            borderRadius: 12
          }}
        >
          {cart.map((item) => (
            <div
              key={item._id}
              style={{
                display: "flex",
                gap: 12,
                padding: "12px 0",
                borderBottom: "1px solid #f3f4f6"
              }}
            >
              <img
                src={item.imageUrl || item.image}
                alt={item.name}
                style={{
                  width: 90,
                  height: 90,
                  borderRadius: 12,
                  objectFit: "cover"
                }}
              />

              <div style={{ flex: 1 }}>
                {Number.isFinite(getMaxQty(item)) ? (
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>
                    Available: {getMaxQty(item)}
                  </div>
                ) : null}
                <div style={{ fontWeight: 700 }}>{item.name}</div>

                <div style={{ fontSize: 14, color: "#6b7280" }}>
                  ₹{item.price}
                </div>
                <div style={{ fontSize: 13, color: "#1d4ed8", marginTop: 6 }}>
                  {getDeliveryLabel(item)}
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    marginTop: 10,
                    alignItems: "center"
                  }}
                >
                  <button
                    onClick={() =>
                      updateQty(item._id, Number(item.qty || 1) - 1)
                    }
                    style={{
                      padding: "6px 10px",
                      borderRadius: 8,
                      border: "1px solid #ddd",
                      background: "#fff",
                      color: "#0f172a",
                      minWidth: 40,
                      cursor: "pointer"
                    }}
                  >
                    -
                  </button>

                  <input
                    value={item.qty || 1}
                    onChange={(e) =>
                      updateQty(item._id, e.target.value)
                    }
                    inputMode="numeric"
                    style={{
                      width: 60,
                      textAlign: "center",
                      padding: 6,
                      borderRadius: 8,
                      border: "1px solid #ddd"
                    }}
                  />

                  <button
                    onClick={() =>
                      updateQty(item._id, Number(item.qty || 1) + 1)
                    }
                    disabled={Number(item.qty || 1) >= getMaxQty(item)}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 8,
                      border: "1px solid #ddd",
                      background: "#fff",
                      color: "#0f172a",
                      minWidth: 40,
                      cursor:
                        Number(item.qty || 1) >= getMaxQty(item)
                          ? "not-allowed"
                          : "pointer",
                      opacity:
                        Number(item.qty || 1) >= getMaxQty(item) ? 0.55 : 1
                    }}
                  >
                    +
                  </button>

                  <button
                    onClick={() => removeItem(item._id)}
                    style={{
                      marginLeft: "auto",
                      padding: "6px 10px",
                      borderRadius: 8,
                      border: "1px solid #ddd",
                      background: "#fff",
                      color: "#0f172a",
                      cursor: "pointer"
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>

              <div style={{ fontWeight: 700 }}>
                ₹{(Number(item.price) * Number(item.qty || 1)).toFixed(2)}
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div
          style={{
            padding: 14,
            border: "1px solid #eee",
            borderRadius: 12
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 700 }}>
            Order Summary
          </div>

          <div style={{ marginTop: 12 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between"
              }}
            >
              <div>Subtotal</div>

              <div>₹{subtotal.toFixed(2)}</div>
            </div>

            <div
              style={{
                marginTop: 8,
                color: "#6b7280",
                fontSize: 13
              }}
            >
              Live fish: Tamil Nadu only. Accessories: all India.
            </div>

            <hr style={{ margin: "12px 0" }} />

            <button
              onClick={() => navigate("/checkout")}
              style={{
                width: "100%",
                padding: 12,
                borderRadius: 10,
                border: "none",
                background: "#111827",
                color: "#fff",
                cursor: "pointer",
                fontSize: 16
              }}
            >
              Proceed to Checkout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
